const clientId = process.env.REACT_APP_CLIENT_ID;

function getRedirectUri() {
  const configuredRedirectUri = process.env.REACT_APP_REDIRECT_URI || process.env.REACT_APP_REDIRECT_ID;

  if (configuredRedirectUri) {
    return configuredRedirectUri;
  }

  return window.location.origin.replace('://localhost', '://127.0.0.1');
}

const redirectUri = getRedirectUri();
const codeVerifierStorageKey = 'spotify_code_verifier';
const tokenStorageKey = 'spotify_access_token';
const tokenExpiryStorageKey = 'spotify_access_token_expires_at';
const refreshTokenStorageKey = 'spotify_refresh_token';
let pendingCodeExchange = null;
let pendingAuthorizationCode = null;

function createRandomString(length) {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const values = crypto.getRandomValues(new Uint8Array(length));

  return values.reduce((accumulator, value) => accumulator + possible[value % possible.length], '');
}

async function sha256(plain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);

  return window.crypto.subtle.digest('SHA-256', data);
}

function base64UrlEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

async function generateCodeChallenge(verifier) {
  const hashedVerifier = await sha256(verifier);

  return base64UrlEncode(hashedVerifier);
}

async function exchangeToken(params) {
  const response = await fetch('https://accounts.spotify.com/api/token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: new URLSearchParams(params)
  });

  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(errorBody || 'Spotify token exchange failed.');
  }

  return response.json();
}

function storeTokenResponse(tokenResponse) {
  if (tokenResponse.access_token) {
    const expiresAt = Date.now() + (tokenResponse.expires_in * 1000);

    sessionStorage.setItem(tokenStorageKey, tokenResponse.access_token);
    sessionStorage.setItem(tokenExpiryStorageKey, String(expiresAt));
  }

  if (tokenResponse.refresh_token) {
    localStorage.setItem(refreshTokenStorageKey, tokenResponse.refresh_token);
  }
}

function clearAuthParams() {
  const url = new URL(window.location.href);

  url.searchParams.delete('code');
  url.searchParams.delete('state');
  url.searchParams.delete('error');

  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);
}

async function refreshAccessToken(refreshToken) {
  const tokenResponse = await exchangeToken({
    client_id: clientId,
    grant_type: 'refresh_token',
    refresh_token: refreshToken
  });

  storeTokenResponse(tokenResponse);

  return tokenResponse.access_token || sessionStorage.getItem(tokenStorageKey);
}

async function exchangeAuthorizationCode(code, codeVerifier) {
  if (pendingCodeExchange && pendingAuthorizationCode === code) {
    return pendingCodeExchange;
  }

  pendingAuthorizationCode = code;
  pendingCodeExchange = exchangeToken({
    client_id: clientId,
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier
  });

  try {
    return await pendingCodeExchange;
  } finally {
    pendingCodeExchange = null;
    pendingAuthorizationCode = null;
  }
}

const spotifyLogin = {
  logInWithSpotify: async () => {
    if (!clientId) {
      console.error('Missing REACT_APP_CLIENT_ID for Spotify login.');
      return;
    }

    const codeVerifier = createRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    sessionStorage.setItem(codeVerifierStorageKey, codeVerifier);

    let scopes = [
      'streaming',
      'user-read-email',
      'user-read-private',
      'user-modify-playback-state'
    ].join(' ');
    

    let scopes_encoded = encodeURIComponent(scopes);


    window.location = [
      'https://accounts.spotify.com/authorize',
      `?client_id=${clientId}`,
      `&redirect_uri=${encodeURIComponent(redirectUri)}`,
      `&scope=${scopes_encoded}`,
      '&response_type=code',
      `&code_challenge_method=S256`,
      `&code_challenge=${codeChallenge}`,
      '&show_dialog=true'
    ].join('');
  },

  getToken: async () => {
    if (!clientId) {
      return '';
    }

    const storedToken = sessionStorage.getItem(tokenStorageKey);
    const storedExpiry = Number(sessionStorage.getItem(tokenExpiryStorageKey) || '0');

    if (storedToken && storedExpiry > Date.now()) {
      return storedToken;
    }

    const refreshToken = localStorage.getItem(refreshTokenStorageKey);
    if (refreshToken) {
      try {
        return await refreshAccessToken(refreshToken);
      } catch (error) {
        console.error('Spotify refresh token failed.', error);
        localStorage.removeItem(refreshTokenStorageKey);
      }
    }

    const url = new URL(window.location.href);
    const code = url.searchParams.get('code');
    const error = url.searchParams.get('error');

    if (error) {
      console.error(`Spotify authorization failed: ${error}`);
      clearAuthParams();
      return '';
    }

    if (!code) {
      return '';
    }

    const codeVerifier = sessionStorage.getItem(codeVerifierStorageKey);
    if (!codeVerifier) {
      console.error('Missing Spotify PKCE code verifier.');
      clearAuthParams();
      return '';
    }

    try {
      const tokenResponse = await exchangeAuthorizationCode(code, codeVerifier);

      storeTokenResponse(tokenResponse);
      sessionStorage.removeItem(codeVerifierStorageKey);
      clearAuthParams();

      return tokenResponse.access_token || '';
    } catch (error) {
      console.error('Spotify token exchange failed.', error);
      sessionStorage.removeItem(codeVerifierStorageKey);
      clearAuthParams();
      return '';
    }
  }
};

export default spotifyLogin;
